import styled, { useTheme } from 'styled-components'
import { Typo, TypoVariant } from '@/components/common/typography'
import { useProductDetailEventTracker } from '@/containers/event-tracker/product-detail.event-tracker-new'

interface ProductDetailContentNavProps {
  onClickNavTab: (tab: string) => void
  activeTab: string
  reviewCount: number
  productName: string
  productId: number
}

const CONTENT_TABS = [
  {
    id: 'info',
    label: '상품정보',
    isCount: false,
  },
  {
    id: 'review',
    label: '리뷰',
    isCount: true,
  },
  {
    id: 'recommend',
    label: '추천',
    isCount: false,
  },
  {
    id: 'inquiry',
    label: '문의',
    isCount: false,
  },
] as const

export const ProductDetailContentNav = ({
  onClickNavTab,
  activeTab,
  reviewCount,
  productName,
  productId,
}: ProductDetailContentNavProps) => {
  const { color } = useTheme()
  const { trackClickProductDetailTab } = useProductDetailEventTracker()

  const handleClickNavItem = (tab: string) => {
    const tabStrKo = CONTENT_TABS.find((t) => t.id === tab)?.label as '상품정보' | '리뷰' | '문의' | '추천'
    trackClickProductDetailTab({ tabName: tabStrKo, productName, productId })
    onClickNavTab(tab)
  }

  const activeIndex = CONTENT_TABS.findIndex((tab) => tab.id === activeTab)

  return (
    <ProductDetailContentNavWrapper
      length={CONTENT_TABS.length}
      className="product-detail-content-nav"
      activeIndex={activeIndex < 0 ? 0 : activeIndex}
    >
      <ul className="product-detail-content-nav__list">
        {CONTENT_TABS.map((tab) => {
          const active = tab.id === activeTab
          const isShowReviewCount = tab.isCount && reviewCount > 0
          return (
            <li key={tab.id} data-tab={`${tab.id}`} className="product-detail-content-nav__item">
              <button
                className="product-detail-content-nav__button"
                onClick={() => handleClickNavItem(tab.id)}
                type="button"
              >
                {active ? (
                  <Typo variant={TypoVariant.Body2Bold} color={color.gray900}>
                    {tab.label}
                  </Typo>
                ) : (
                  <Typo variant={TypoVariant.Body2Regular} color={color.gray500}>
                    {tab.label}
                  </Typo>
                )}
                {isShowReviewCount && (
                  <div className="product-detail-content-nav__count">
                    <Typo variant={TypoVariant.Body5Regular} color={color.gray300}>
                      {`${reviewCount.toLocaleString('ko-KR')}`}
                    </Typo>
                  </div>
                )}
              </button>
            </li>
          )
        })}
      </ul>
      <div className="product-detail-content-nav__under-line"></div>
    </ProductDetailContentNavWrapper>
  )
}

const ProductDetailContentNavWrapper = styled.nav<{ activeIndex: number; length: number }>`
  position: relative;
  .product-detail-content-nav {
    &__list {
      margin: 0;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
    }
    &__item {
      flex: 1;
      text-align: center;
    }
    &__button {
      border: none;
      outline: none;
      background-color: transparent;
      cursor: pointer;
      width: 100%;
      padding: 0.9rem 1.2rem 1.9rem;
      position: relative;
    }
    &__count {
      bottom: 0.4rem;
      left: 50%;
      transform: translateX(-50%);
      position: absolute;
    }
    &__under-line {
      position: absolute;
      bottom: 0;
      left: 0;
      width: calc(100% / ${({ length }) => length});
      height: 0.2rem;
      background-color: ${({ theme }) => theme.color.gray900};
      transform: translateX(${({ activeIndex }) => activeIndex * 100}%) translateY(0.2rem);
      transition: transform 0.3s;
    }
    &__count {
      display: flex;
      justify-content: center;
    }
  }
`
